import { captureTracking } from "./applicationAttribution";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, initGA4, trackEvent } from "./analytics";

/* ============================================================
 * <AnalyticsTracker /> — mount once inside <BrowserRouter>.
 *
 * Responsibilities:
 *  1. Initialize GA4 (if REACT_APP_GA4_ID is configured).
 *  2. Fire `page_view` on every SPA route change.
 *  3. Global click delegator — captures clicks on any [data-testid]
 *     element and fires a `cta_click` event (kebab-case testid is the label).
 *  4. Outbound link tracker — flags clicks on external (non-same-origin)
 *     anchors as `outbound_click`.
 *  5. Form submit tracker — captures any form with [data-testid] submission.
 * ============================================================ */

const SAME_ORIGIN = (href) => {
  try {
    const u = new URL(href, window.location.href);
    return u.origin === window.location.origin;
  } catch (_) {
    return true;
  }
};

const labelForElement = (el) => {
  // Prefer aria-label, then visible text, capped at 80 chars
  const aria = el.getAttribute && el.getAttribute("aria-label");
  if (aria) return aria.slice(0, 80);
  const txt = (el.innerText || el.textContent || "").trim().replace(/\s+/g, " ");
  return txt.slice(0, 80) || null;
};

export default function AnalyticsTracker() {
  const location = useLocation();

  // Init GA4 once on mount
  useEffect(() => {
    captureTracking();
    initGA4();
  }, []);

  // Fire page_view on each route change
  useEffect(() => {
    // Defer one tick so document.title (set by react-helmet) is up-to-date
    const id = setTimeout(() => {
      trackPageView(location.pathname);
    }, 50);
    return () => clearTimeout(id);
  }, [location.pathname]);

  // Global click delegator
  useEffect(() => {
    const onClick = (e) => {
      const target = e.target;
      if (!target || !target.closest) return;

      // 1) Buttons / links / any element with data-testid → cta_click
      const trackable = target.closest("[data-testid]");
      if (trackable) {
        const testid = trackable.getAttribute("data-testid");
        // Avoid noisy testids — skip generic containers
        const isInteractive =
          trackable.tagName === "A" ||
          trackable.tagName === "BUTTON" ||
          trackable.getAttribute("role") === "button" ||
          trackable.tagName === "INPUT" ||
          trackable.tagName === "LABEL" ||
          trackable.hasAttribute("onclick") ||
          (trackable.classList && trackable.classList.contains("cursor-pointer"));

        if (isInteractive) {
          const href = trackable.getAttribute("href") || null;
          trackEvent("cta_click", {
            metadata: {
              testid,
              tag: trackable.tagName.toLowerCase(),
              href,
              label: labelForElement(trackable),
            },
          });
        }
      }

      // 2) Outbound link tracker
      const a = target.closest("a[href]");
      if (a) {
        const href = a.getAttribute("href");
        if (href && /^https?:/i.test(href) && !SAME_ORIGIN(href)) {
          trackEvent("outbound_click", {
            metadata: {
              href,
              host: (() => { try { return new URL(href).host; } catch (_) { return null; } })(),
              label: labelForElement(a),
              testid: a.getAttribute("data-testid") || null,
            },
          });
        }
      }
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  // Global form submit tracker
  useEffect(() => {
    const onSubmit = (e) => {
      const form = e.target;
      if (!form || form.tagName !== "FORM") return;
      const testid = form.getAttribute("data-testid") || null;
      if (!testid) return; // only track named forms
      trackEvent("form_submit", {
        metadata: {
          testid,
          action: form.getAttribute("action") || null,
        },
      });
    };
    document.addEventListener("submit", onSubmit, { capture: true });
    return () => document.removeEventListener("submit", onSubmit, { capture: true });
  }, []);

  // Scroll-depth tracker (one-shot @25/50/75/100%)
  useEffect(() => {
    const fired = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / Math.max(doc.scrollHeight, 1);
      [0.25, 0.5, 0.75, 1.0].forEach((threshold) => {
        if (scrolled >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          trackEvent("scroll_depth", { metadata: { percent: Math.round(threshold * 100) } });
        }
      });
    };
    // Reset on route change
    fired.clear();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  return null;
}
